import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Shield, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ForcePasswordChangeModal
 *
 * Shown when the backend signals `password_change_required=true` for the
 * current user (typically after an admin force-reset). Blocking, no-close.
 *
 * On submit, calls `/auth/change-password`. On success, the backend clears
 * the flag and invalidates all tokens — we hard-redirect to login so a
 * fresh session is established.
 */
const ForcePasswordChangeModal = ({ open, currentEmail }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!currentPassword || !newPassword) {
      setError('Both fields are required');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the temporary password');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed. Please log in again with your new password.');
      // Force a clean session: clear local state and bounce to login.
      localStorage.removeItem('token');
      localStorage.removeItem('session_id');
      delete axios.defaults.headers.common['Authorization'];
      setTimeout(() => {
        window.location.href = '/';
      }, 800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="force-password-change-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0A7EA4]" />
            Choose a new password
          </DialogTitle>
          <DialogDescription>
            Your password was reset by an administrator. For security, you must
            choose a new password before continuing.
            {currentEmail ? (
              <span className="block mt-2 text-xs text-gray-500">Account: {currentEmail}</span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fp-current">Temporary password (from email)</Label>
            <Input
              id="fp-current"
              data-testid="fp-current-input"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Paste the temporary password"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="fp-new">New password</Label>
            <Input
              id="fp-new"
              data-testid="fp-new-input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <Label htmlFor="fp-confirm">Confirm new password</Label>
            <Input
              id="fp-confirm"
              data-testid="fp-confirm-input"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          {error ? (
            <div
              data-testid="fp-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <Button
            type="submit"
            data-testid="fp-submit-btn"
            disabled={loading}
            className="w-full bg-[#0A7EA4] hover:bg-[#086586]"
          >
            {loading ? 'Updating…' : 'Update password & continue'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChangeModal;
